import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import SERVICE_API_URL from '../serviceUrl';

const useTestFetching = ({ topic = 'MATH', testType, endpoint = 'latest', pageToken = null }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageTokens, setPrevPageTokens] = useState(new Map());
  const [currentPageToken, setCurrentPageToken] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [isFirstPage, setIsFirstPage] = useState(true);

  const fetchTests = async (token = null) => {
    setLoading(true);
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    try {
      const params = {
        topic,
        lastPageToken: token ? JSON.stringify(token) : null
      };

      if (testType) {
        params.testType = testType;
      }

      // Use endpoint parameter to determine which endpoint to call
      let apiEndpoint;
      if (endpoint === 'latest') {
        apiEndpoint = `${SERVICE_API_URL}/prep/latest`;
      } else if (endpoint === 'samples') {
        apiEndpoint = `${SERVICE_API_URL}/prep/samples`;
      } else {
        apiEndpoint = `${SERVICE_API_URL}/prep/tests`;
      }

      const response = await axios.get(apiEndpoint, {
        params,
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });

      if (endpoint === 'latest') {
        // Handle /latest endpoint response (can return single or multiple tests)
        const testData = Array.isArray(response.data) ? response.data : [response.data];
        setTests(testData.filter(Boolean));
        setNextPageToken(null);
        setIsLastPage(true);
        setIsFirstPage(true);
      } else {
        // Handle paginated response from /tests or /samples endpoint
        setTests(response.data.testList || response.data || []);
        setNextPageToken(response.data.nextPageToken);

        if (response.data.nextPageToken != null) {
          if (token === null) {
            prevPageTokens.set(response.data.nextPageToken, 1);
            prevPageTokens.set(1, null);
          } else {
            prevPageTokens.set(response.data.nextPageToken, token);
          }
        }

        setCurrentPageToken(token);
        setIsLastPage(response.data.nextPageToken === null);
        setIsFirstPage(token === null);
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching tests:", error);
      setError("Failed to fetch tests. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextPage = () => {
    if (nextPageToken) {
      fetchTests(nextPageToken);
    }
  };

  const fetchPrevPage = () => {
    const prevToken = prevPageTokens.get(currentPageToken);
    if (prevToken === 1) {
      fetchTests(null);
    } else if (prevToken) {
      fetchTests(prevToken);
    }
  };

  useEffect(() => {
    fetchTests(pageToken);
  }, [topic, testType, endpoint]); // Re-fetch when topic, testType, or endpoint changes

  return {
    tests,
    loading,
    error,
    nextPageToken,
    isLastPage,
    isFirstPage,
    fetchNextPage,
    fetchPrevPage,
    refreshTests: () => fetchTests(pageToken)
  };
};

export default useTestFetching; 